/**
 * The scribe-layout CSAT popover's analytics vocabulary: the `view_action` and
 * `target_type` tokens it reports against, the payloads its two events carry, and
 * the emit seams the app binds.
 *
 * WHAT IS HERE AND WHAT IS NOT. The tokens and the payloads are contract — the
 * whole population of this survey is `view_action = 'scribe_layout_csat_shown'`
 * and `target_type = 'scribe_layout_v2'`, so a second spelling of either is a
 * survey with no respondents. The EMIT is not, and neither are the event NAMES:
 * these land as `survey_viewed` and `feedback_submitted`, both cross-domain —
 * `feedback_submitted` alone is reported by eight surfaces across chat, evidence,
 * the document inbox and the editor — so no domain declares either name (D9
 * ruling 27) and there is no descriptor for them in
 * `src/contributions/analytics.ts`.
 *
 * `target_type` is a WIDE app-side union (`FeedbackTargetType` plus four
 * FE-only strings) precisely because it is shared. Growth owns one member of it,
 * and the app-side host asserts the token is that member.
 */

/** The `view_action` token this survey's impression carries. */
export const SCRIBE_LAYOUT_CSAT_SURVEY_ACTION = {
  shown: 'scribe_layout_csat_shown',
} as const;

export type ScribeLayoutCsatSurveyActionToken =
  (typeof SCRIBE_LAYOUT_CSAT_SURVEY_ACTION)[keyof typeof SCRIBE_LAYOUT_CSAT_SURVEY_ACTION];

/** The `target_type` token this survey's submission carries. */
export const SCRIBE_LAYOUT_CSAT_FEEDBACK_TARGET = {
  scribeLayoutV2: 'scribe_layout_v2',
} as const;

export type ScribeLayoutCsatFeedbackTargetToken =
  (typeof SCRIBE_LAYOUT_CSAT_FEEDBACK_TARGET)[keyof typeof SCRIBE_LAYOUT_CSAT_FEEDBACK_TARGET];

/** `survey_viewed` — the popover became visible. */
export type ScribeLayoutCsatViewedProps = {
  view_action: ScribeLayoutCsatSurveyActionToken;
};

/**
 * `feedback_submitted` — the clinician submitted a star rating.
 *
 * `value` is the numeric `CSATRating` the form resolved, kept as `number` rather
 * than reaching for the generated enum: the wire property has always been a
 * number, this door may value-import only `@heidi/schema`, and narrowing it to
 * the enum would make a legacy rating outside the enum's range a compile error at
 * a call site rather than a data question.
 *
 * `feedback` is absent, not empty, when the clinician typed nothing — an empty
 * string is a submitted blank comment and the two must not share a bucket.
 */
export type ScribeLayoutCsatSubmittedProps = {
  value: number;
  feedback?: string;
  target_type: ScribeLayoutCsatFeedbackTargetToken;
};

/** The emit seam for the popover being shown. */
export type TrackScribeLayoutCsatViewed = (props: ScribeLayoutCsatViewedProps) => void;

/** The emit seam for a submitted rating. */
export type TrackScribeLayoutCsatSubmitted = (
  props: ScribeLayoutCsatSubmittedProps
) => void;

/**
 * Both seams as one app-supplied bundle.
 *
 * The impression and the submission are one surface's pair — the popover fires
 * both — so they cross together rather than as two half-seams.
 */
export type ScribeLayoutCsatAnalyticsPorts = {
  trackViewed: TrackScribeLayoutCsatViewed;
  trackSubmitted: TrackScribeLayoutCsatSubmitted;
};
